/**
 * Shadow plans API. Plans are read-only snapshots of what the planner would have done;
 * all Date fields arrive as ISO strings.
 */

import api from './api';

export interface ShadowPlanStop {
  seq: number;
  kind: 'pickup' | 'rto' | 'visit' | 'cash';
  merchantId: number | null;
  merchantName: string | null;
  lat: number;
  lng: number;
  eta: string;
  windowStart: string;
  windowEnd: string;
  orders: number;
  rtoParcels: number;
  isForecast: boolean;
  isCold?: boolean; // orders that need a refrigerated van; absent on plans saved before 2026-10-27
  taskUids: string[];
}

export interface ShadowPlanDriver {
  label: string;
  vehicleType: 'SHIFT1' | 'SHIFT2' | 'COLD';
  startAt: string;
  endAt: string;
  km: number;
  orders: number;
  stops: ShadowPlanStop[];
}

export interface ShadowPlan {
  opDate: string;
  kind: string;
  generatedAt: string;
  fingerprint: string;
  chosen: 'fresh' | 'kept';
  summary: {
    drivers: number;
    stops: number;
    pickups: number;
    forecastStops: number;
    rtoDrops: number;
    cash: number;
    unassigned: number;
  };
  drivers: ShadowPlanDriver[];
  unassigned: { key: string; merchantId: number | null; reason: string }[];
}

export interface ShadowDay {
  morning: ShadowPlan | null;
  latest: ShadowPlan | null;
}

export const shadowPlansApi = {
  async getDay(date: string): Promise<ShadowDay> {
    const response = await api.get<{ success: boolean; data: ShadowDay }>('/shadow-plans', { params: { date } });
    return response.data.data;
  },

  async listDates(): Promise<string[]> {
    const response = await api.get<{ success: boolean; data: string[] }>('/shadow-plans/dates');
    return response.data.data;
  },
};
